import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { BasecampLayout, AreaLayout } from '../../components/Layout';
import { PageHeader, Card, Spinner, Alert, Badge, Table, Input, Select, EmptyState, Btn, Modal, Breadcrumb, SectionLabel, SectionCard, Note } from '../../components/UI';
import { api, getUser } from '../../utils/api';
import PhaseOverview from '../phase/PhaseOverview';
import PhaseNotes from '../phase/PhaseNotes';
import { TranslateButton } from '../../components/TranslatePanel';

// Deliver — the training room. Faza 1: "Learn the HB content" (read-only
// biblioteka). Faza 3C: "Deliver for a client" (My Client Deliveries) —
// zatvoreni engagementi sa day materials paketom i "Close the day".
// Struktura podsekcija prati mockup SUBNAV.deliver.

const SUBNAV = [
  { href: '/deliver/overview', label: 'Ice Pilot in Delivery', isHeaderLink: true },
  { header: 'Learn the HB content' },
  { href: '/deliver/units', label: 'Units', indent: true },
  { href: '/deliver/progs', label: 'Development Programmes', indent: true },
  { href: '/deliver/logs', label: 'Expedition Logs', indent: true },
  { href: '/deliver/mcs', label: 'Memory Cards', indent: true },
  { href: '/deliver/fcs', label: 'Flipchart Drawings', indent: true },
  { href: '/deliver/ws', label: 'Working Sheets', indent: true },
  { header: 'Deliver for a client' },
  { href: '/deliver/client', label: 'My Client Deliverables', indent: true },
  { href: '/deliver/proposals', label: 'Proposals', indent: true },
];

function useFetch(fn, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fn().then(d => { if (alive) { setData(d); setLoading(false); } })
      .catch(e => { if (alive) { setError(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, deps);
  return { data, error, loading };
}

function Loading() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner /></div>;
}

// Chip za weighted facet tag — w3 najjaci, kao u mockup-u
function TagChip({ tag }) {
  const weights = { 3: 'var(--ink)', 2: 'var(--ink-soft)', 1: 'var(--ink-faint)' };
  return (
    <span title={tag.Rationale || ''} style={{
      display: 'inline-block', padding: '2px 9px', borderRadius: '99px', margin: '2px 4px 2px 0',
      fontSize: '0.72rem', fontWeight: 600, border: `1px solid ${weights[tag.Weight] || 'var(--ink-faint)'}`,
      color: weights[tag.Weight] || 'var(--ink-faint)',
    }}>
      {tag.FacetName} · {tag.Weight}
    </span>
  );
}

// Status badge — Standard / Ready to Use / Idea (retires "CANON"); DRAFT shown alongside.
function StatusBadge({ status, isDraft }) {
  // Retire the old "canon"/"Standards" vocabulary even on rows not yet re-imported.
  const raw = (status || '').toLowerCase();
  const s = (!status || raw === 'canon' || raw === 'standards' || raw === 'approved') ? 'Standard' : status;
  const tone = s === 'Standard' ? 'completed' : s === 'Ready to Use' ? 'active' : 'default';
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      <Badge status={tone}>{s}</Badge>
      {isDraft && <Badge status="pending">DRAFT</Badge>}
    </span>
  );
}

// Superadmin: pull the artefact files from OneDrive (Graph) into Basecamp.
function SyncMaterialsButton() {
  const isSuper = (getUser()?.roles || []).includes('SUPERADMIN');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  if (!isSuper) return null;
  async function run() {
    setBusy(true); setMsg(null);
    try {
      const r = await api.admin.syncMaterials();
      setMsg(`Synced: ${r.stored} stored, ${r.skippedUnchanged || 0} unchanged, ${r.matched} matched${r.errors ? `, ${r.errors} errors` : ''}${r.skippedTooBig ? `, ${r.skippedTooBig} too large` : ''}.`);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  }
  return (
    <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {msg && <span style={{ fontSize: '0.72rem', color: 'var(--ink-faint)' }}>{msg}</span>}
      <Btn size="sm" variant="outline" loading={busy} onClick={run} title="Pull artefact files from the OneDrive Training Materials folder">
        Sync materials from OneDrive
      </Btn>
    </span>
  );
}

// ── Units katalog ──────────────────────────────────────────────────────────
function UnitsCatalogue() {
  const { data, error, loading } = useFetch(() => api.getUnits());
  const [q, setQ] = useState('');
  const [family, setFamily] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const navigate = useNavigate();
  const isSuper = (getUser()?.roles || []).includes('SUPERADMIN');

  if (loading) return <Loading />;
  if (error) return <Alert type="error">{error}</Alert>;

  const units = (data?.units || []).filter(u => {
    const text = `${u.code} ${u.name} ${u.purpose || ''}`.toLowerCase();
    return text.includes(q.toLowerCase()) && (!family || u.family === family);
  });
  const families = [...new Set((data?.units || []).map(u => u.family))];

  return (
    <>
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input placeholder="Filter catalogue…" value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 320 }} />
        <Select value={family} onChange={e => setFamily(e.target.value)} style={{ maxWidth: 180 }}>
          <option value="">All families</option>
          {families.map(f => <option key={f} value={f}>{f}</option>)}
        </Select>
        <span style={{ fontSize: '0.78rem', color: 'var(--ink-faint)' }}>{units.length} unit(s)</span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
          {isSuper && <Btn size="sm" variant="outline" onClick={() => setNewOpen(true)}>+ New unit</Btn>}
          <SyncMaterialsButton />
        </span>
      </div>
      {newOpen && <NewUnitModal onClose={() => setNewOpen(false)} onCreated={code => navigate(`/deliver/units/${code}`)} />}
      {units.length === 0 && <EmptyState title="No Units match" message="Try a different filter." />}
      <div style={{ display: 'grid', gap: 14 }}>
        {units.map(u => (
          <Card key={u.code} hover style={{ padding: '16px 20px' }}>
            <Link to={`/deliver/units/${u.code}`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.02rem', fontWeight: 700 }}>
                  ({u.code}) {u.name}
                </span>
                {!data.teaser && <StatusBadge status={u.status} isDraft={u.isDraft} />}
                {!data.teaser && u.stream && <span style={{ fontSize: '0.68rem', color: 'var(--ink-faint)' }}>{u.stream}</span>}
              </div>
              <div style={{ fontSize: '0.86rem', color: 'var(--ink-soft)', lineHeight: 1.55, marginBottom: 8 }}>
                {(u.purpose || '').slice(0, 220)}{(u.purpose || '').length > 220 ? '…' : ''}
              </div>
              {!data.teaser && (
                <div style={{ fontSize: '0.75rem', color: 'var(--ink-faint)' }}>
                  FC: {u.flipcharts || '—'} · sheets: {u.sheets || '—'} · facet tags: {u.tagCount}
                  {u.custodianTeam ? ` · custodian: ${u.custodianTeam}` : ' · custodian: pending August roster'} · craft: by observation
                </div>
              )}
            </Link>
          </Card>
        ))}
      </div>
    </>
  );
}

// ── Unit detail ────────────────────────────────────────────────────────────
function Block({ title, children }) {
  if (!children) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <SectionLabel>{title}</SectionLabel>
      <div style={{ fontSize: '0.9rem', lineHeight: 1.65, color: 'var(--ink-mid)', whiteSpace: 'pre-wrap' }}>{children}</div>
    </div>
  );
}

// ── Unit record — renders inside the Deliver shell, with its own section menu ─
const secH = { fontFamily: 'var(--font-display)', fontSize: '1.15rem', fontWeight: 700, marginBottom: 8 };
const prose = { fontSize: '0.9rem', lineHeight: 1.65, color: 'var(--ink-mid)', whiteSpace: 'pre-wrap' };
const inUnitTab = { display: 'block', width: '100%', textAlign: 'left', fontSize: '0.82rem', color: 'var(--ink-soft)', padding: '7px 10px', borderRadius: 'var(--radius-sm)', marginBottom: 2, cursor: 'pointer', border: 'none', background: 'none', fontFamily: 'var(--font-body)' };
const inUnitTabOn = { background: 'var(--ink)', color: '#fff', fontWeight: 600 };
const dlLink = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--ink)', textDecoration: 'underline', fontSize: '0.74rem', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap' };

// Module-level so the section nodes are stable across re-renders (otherwise the
// scrollspy IntersectionObserver keeps watching detached nodes and freezes on the first).
function Sec({ id, refs, children }) {
  return (
    <section id={id} ref={el => { refs.current[id] = el; }} style={{ scrollMarginTop: 80, marginBottom: 16 }}>
      <Card style={{ padding: '20px 22px' }}>{children}</Card>
    </section>
  );
}

// Superadmin: pull just THIS unit's files from OneDrive.
function UnitSyncButton({ code, onDone }) {
  const isSuper = (getUser()?.roles || []).includes('SUPERADMIN');
  const [busy, setBusy] = useState(false);
  if (!isSuper) return null;
  async function run() {
    setBusy(true);
    try {
      const r = await api.admin.syncMaterials(code);
      onDone && onDone();
      alert(`Synced ${code}: ${r.stored} stored, ${r.skippedUnchanged || 0} unchanged, ${r.matched} matched${r.errors ? `, ${r.errors} errors` : ''}.`);
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  return <Btn size="sm" variant="outline" loading={busy} onClick={run} title="Pull this unit's files from OneDrive">Sync this unit</Btn>;
}

export function UnitRecord({ code }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const { data, error, loading } = useFetch(() => api.getUnit(code), [code, refreshKey]);
  const [active, setActive] = useState(null);
  const [lb, setLb] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const refs = useRef({});
  const isSuper = (getUser()?.roles || []).includes('SUPERADMIN');
  const u = data?.unit;

  const sections = useMemo(() => {
    if (!u) return [];
    const a = data.artifacts || {};
    return [
      (u.purpose || u.storyline || u.participants) && { id: 'about', label: 'About the Unit' },
      u.mapping && { id: 'compass', label: 'Compass Mapping' },
      (u.readyToSay || u.readyToSaySales) && { id: 'ready', label: 'Ready-to-Say' },
      u.diagnosticQuestions && { id: 'diagnostic', label: 'Diagnostic Questions' },
      u.benefits && { id: 'benefits', label: 'Benefits' },
      (a.Script || []).length && { id: 'delivery', label: 'How it’s delivered' },
      { id: 'materials', label: 'Materials' },
      { id: 'relations', label: 'Relations' },
      { id: 'facts', label: 'Quick Facts' },
    ].filter(Boolean);
  }, [u, data]);

  useEffect(() => {
    if (!sections.length) return;
    const obs = new IntersectionObserver((entries) => {
      const vis = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActive(vis[0].target.id);
    }, { rootMargin: '-80px 0px -65% 0px', threshold: 0 });
    sections.forEach(s => { const el = refs.current[s.id]; if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [sections]);

  function go(id) { refs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' }); setActive(id); }

  function startEdit() {
    setForm({
      name: u.name || '', status: u.status || '', custodianTeam: u.custodianTeam || '',
      purpose: u.purpose || '', storyline: u.storyline || '', mapping: u.mapping || '',
      readyToSay: u.readyToSay || '', readyToSaySales: u.readyToSaySales || '',
      diagnosticQuestions: u.diagnosticQuestions || '', benefits: u.benefits || '',
      participants: u.participants || '', dependencies: u.dependencies || '',
    });
    setEditing(true);
  }
  async function saveEdit() {
    setSaving(true);
    try { await api.admin.updateUnit(code, form); setEditing(false); setRefreshKey(k => k + 1); }
    catch (e) { alert(e.message); } finally { setSaving(false); }
  }
  async function revert() {
    if (!window.confirm('Revert this unit to the registry text? Your on-site edits will be replaced from units_registry.json on the next import.')) return;
    try { await api.admin.revertUnit(code); setRefreshKey(k => k + 1); } catch (e) { alert(e.message); }
  }

  if (loading) return <Loading />;
  if (error) return <Alert type="error">{error}</Alert>;
  const arts = data.artifacts || {};

  return (
    <div>
      {/* Header */}
      <Breadcrumb items={[{ label: 'Units', to: '/deliver/units' }, { label: `(${u.code}) ${u.name}` }]} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700 }}>({u.code}) {u.name}</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--ink-soft)', marginTop: 2 }}>Unit record · family {u.family}{u.stream ? ` · ${u.stream}` : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isSuper && !editing && <Btn size="sm" variant="outline" onClick={startEdit}>Edit content</Btn>}
          {!editing && <TranslateButton type="unit" entityKey={u.code} />}
          <UnitSyncButton code={code} onDone={() => setRefreshKey(k => k + 1)} />
        </div>
      </div>
      {u.contentOverridden && !editing && (
        <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', background: 'var(--canvas-warm)', borderRadius: 'var(--radius-sm)', padding: '7px 12px', marginBottom: 12 }}>
          ✎ Edited on site — registry text updates are paused for this unit.
          {isSuper && <button onClick={revert} style={{ background: 'none', border: 'none', padding: 0, marginLeft: 8, cursor: 'pointer', color: 'var(--ink)', textDecoration: 'underline', fontSize: '0.8rem', fontFamily: 'var(--font-body)' }}>Revert to registry</button>}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, alignItems: 'center', flexWrap: 'wrap' }}>
        <StatusBadge status={u.status} isDraft={u.isDraft} />
        <Badge status="default">{u.visibility || 'internal'}</Badge>
        {u.version && <Badge status="default">v{u.version}</Badge>}
        <span style={{ fontSize: '0.75rem', color: 'var(--ink-faint)' }}>custodian: {u.custodianTeam || 'pending August roster'}</span>
      </div>

      {editing ? (
        <UnitEditForm form={form} setForm={setForm} onSave={saveEdit} onCancel={() => setEditing(false)} saving={saving} />
      ) : (
      /* In-unit section menu (third menu) + content */
      <div style={{ display: 'flex', gap: 26, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ width: 176, flexShrink: 0, position: 'sticky', top: 36, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {sections.map(s => (
            <button key={s.id} onClick={() => go(s.id)} style={{ ...inUnitTab, ...(active === s.id ? inUnitTabOn : {}) }}>{s.label}</button>
          ))}
        </div>

        <div style={{ flex: 1, minWidth: 300, maxWidth: 760 }}>
          {(u.purpose || u.storyline || u.participants) && (
            <Sec refs={refs} id="about">
              <h2 style={secH}>About the Unit</h2>
              <Block title="Purpose">{u.purpose}</Block>
              <Block title="Storyline">{u.storyline}</Block>
              <Block title="Participants">{u.participants}</Block>
            </Sec>
          )}
          {u.mapping && <Sec refs={refs} id="compass"><h2 style={secH}>HB Compass Mapping</h2><div style={prose}>{u.mapping}</div></Sec>}
          {(u.readyToSay || u.readyToSaySales) && (
            <Sec refs={refs} id="ready">
              <h2 style={secH}>Ready-to-Say</h2>
              <Block title="Kickoff">{u.readyToSay}</Block>
              <Block title="Sales">{u.readyToSaySales}</Block>
            </Sec>
          )}
          {u.diagnosticQuestions && <Sec refs={refs} id="diagnostic"><h2 style={secH}>Diagnostic Questions</h2><div style={prose}>{u.diagnosticQuestions}</div></Sec>}
          {u.benefits && <Sec refs={refs} id="benefits"><h2 style={secH}>Benefits for the Company</h2><div style={prose}>{u.benefits}</div></Sec>}

          {(arts.Script || []).length > 0 && (
            <Sec refs={refs} id="delivery">
              <h2 style={secH}>How the Unit is delivered</h2>
              <p style={{ fontSize: '0.82rem', color: 'var(--ink-faint)', marginBottom: 8 }}>The delivery script (PowerPoint) — how the Unit is run in the room.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>{(arts.Script).map((f, i) => <ArtifactRow key={f.id || i} f={f} />)}</div>
            </Sec>
          )}

          <Sec refs={refs} id="materials">
            <h2 style={secH}>Materials</h2>
            <CoverageLine arts={arts} />
            {visibleFiles(arts.FC).length > 0 && (
              <>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, margin: '14px 0 6px' }}>Flipchart drawings <span style={{ color: 'var(--ink-faint)' }}>({visibleFiles(arts.FC).length})</span></div>
                <Gallery files={arts.FC} onOpen={setLb} />
              </>
            )}
            <ArtefactList arts={arts} />
            {!Object.keys(arts).length && <div style={{ fontSize: '0.85rem', color: 'var(--ink-faint)' }}>No artefacts recorded yet — the gaps are the message.</div>}
          </Sec>

          <Sec refs={refs} id="relations">
            <h2 style={secH}>Relations</h2>
            <Block title="Connections to other units">{u.dependencies}</Block>
            <Block title="Memory cards handed out">{(u.memoryCardsListed || []).length ? (u.memoryCardsListed || []).join(' · ') : null}</Block>
            <Block title="Used in programmes">
              {data.usage.length
                ? data.usage.map((x, i) => <div key={i}>▪ {x.Programme} — day {x.DayNo}</div>)
                : ((u.programmesListed || []).length ? (u.programmesListed || []).join(' · ') : '—')}
            </Block>
            <Block title={`Weighted facet tags (${data.tags.length})`}><div>{data.tags.map((t, i) => <TagChip key={i} tag={t} />)}</div></Block>
          </Sec>

          <Sec refs={refs} id="facts">
            <h2 style={secH}>Quick Facts</h2>
            <div style={{ fontSize: '0.88rem', lineHeight: 1.9, color: 'var(--ink-soft)' }}>
              <div>Family: <b style={{ color: 'var(--ink)' }}>{u.family}</b>{u.stream ? ` · ${u.stream}` : ''}</div>
              <div>Status: <b style={{ color: 'var(--ink)' }}>{u.status || 'Standard'}</b>{u.isDraft ? ' · DRAFT' : ''}{u.version ? ` · v${u.version}` : ''}</div>
              <div>Custodian: <b style={{ color: 'var(--ink)' }}>{u.custodianTeam || 'pending August roster'}</b></div>
              <div>Coverage: {coverageText(arts)}</div>
            </div>
            <div style={{ marginTop: 12, fontSize: '0.82rem', color: 'var(--ink-faint)', fontStyle: 'italic' }}>
              The craft is never digital — it is transmitted in person, master to trainer. {u.masters ? `Masters: ${u.masters}.` : 'Ask to observe a delivery.'}
            </div>
          </Sec>
        </div>
      </div>
      )}
      <Lightbox artifact={lb} onClose={() => setLb(null)} />
    </div>
  );
}

// Editable text fields (superadmin). Saving marks the unit as edited-on-site so the
// registry import won't overwrite it (until "Revert to registry").
const UNIT_EDIT_LBL = { display: 'block', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: 4 };
const UNIT_EDIT_FIELDS = [
  ['purpose', 'Purpose'], ['storyline', 'Storyline'], ['mapping', 'HB Compass Mapping'],
  ['readyToSay', 'Ready to say — kickoff'], ['readyToSaySales', 'Ready to say — sales'],
  ['diagnosticQuestions', 'Diagnostic questions'], ['benefits', 'Benefits for the company'],
  ['participants', 'Participants'], ['dependencies', 'Connections to other units'],
];
function UnitEditForm({ form, setForm, onSave, onCancel, saving }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const ta = { width: '100%', minHeight: 84, padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--line)', fontFamily: 'var(--font-body)', fontSize: '0.88rem', lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box' };
  return (
    <Card style={{ padding: '20px 22px', maxWidth: 820 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 14 }}>
        <div><label style={UNIT_EDIT_LBL}>Name</label><Input value={form.name} onChange={e => set('name', e.target.value)} /></div>
        <div><label style={UNIT_EDIT_LBL}>Status</label>
          <Select value={form.status} onChange={e => set('status', e.target.value)}>
            <option value="">—</option>{['Standard', 'Ready to Use', 'Idea'].map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
        </div>
        <div><label style={UNIT_EDIT_LBL}>Custodian team</label><Input value={form.custodianTeam} onChange={e => set('custodianTeam', e.target.value)} /></div>
      </div>
      {UNIT_EDIT_FIELDS.map(([k, label]) => (
        <div key={k} style={{ marginBottom: 12 }}>
          <label style={UNIT_EDIT_LBL}>{label}</label>
          <textarea value={form[k]} onChange={e => set(k, e.target.value)} style={ta} />
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <Btn size="sm" loading={saving} onClick={onSave}>Save changes</Btn>
        <Btn size="sm" variant="outline" onClick={onCancel}>Cancel</Btn>
      </div>
    </Card>
  );
}

// Create a NEW unit on the site (superadmin). It's marked edited-on-site so the registry
// import won't touch it; attach its files afterwards via the folder + materials sync.
function NewUnitModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    code: '', name: '', status: 'Idea', purpose: '', storyline: '', mapping: '',
    readyToSay: '', readyToSaySales: '', diagnosticQuestions: '', benefits: '', participants: '', dependencies: '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const ta = { width: '100%', minHeight: 70, padding: '9px 11px', borderRadius: 'var(--radius-md)', border: '1px solid var(--line)', fontFamily: 'var(--font-body)', fontSize: '0.86rem', lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box' };
  async function create() {
    if (!/^[A-Za-z]{2,4}\d{2,3}$/.test(form.code.trim())) { alert('Code must look like IE099 / ML214 / SE210.'); return; }
    if (!form.name.trim()) { alert('Name is required.'); return; }
    setBusy(true);
    try { const r = await api.admin.createUnit(form); onCreated(r.code); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  return (
    <Modal open onClose={onClose} title="New unit">
      <p style={{ fontSize: '0.82rem', color: 'var(--ink-soft)', marginBottom: 12 }}>
        Creates the unit record here. Its files (flipcharts, cards, script…) attach afterwards from the OneDrive folder via <b>Sync materials</b>, keyed by the code.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 12 }}>
        <div><label style={UNIT_EDIT_LBL}>Code *</label><Input value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} placeholder="IE099" /></div>
        <div style={{ gridColumn: 'span 2' }}><label style={UNIT_EDIT_LBL}>Name *</label><Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Unit name" /></div>
        <div><label style={UNIT_EDIT_LBL}>Status</label>
          <Select value={form.status} onChange={e => set('status', e.target.value)}>{['Standard', 'Ready to Use', 'Idea'].map(s => <option key={s} value={s}>{s}</option>)}</Select>
        </div>
      </div>
      {UNIT_EDIT_FIELDS.map(([k, label]) => (
        <div key={k} style={{ marginBottom: 10 }}>
          <label style={UNIT_EDIT_LBL}>{label}</label>
          <textarea value={form[k]} onChange={e => set(k, e.target.value)} style={ta} />
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <Btn size="sm" loading={busy} onClick={create}>Create unit</Btn>
        <Btn size="sm" variant="outline" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}

// Coverage helpers
function coverageText(a) {
  const n = (k) => visibleFiles(a[k]).length;
  return [`FC ${n('FC') || '—'}`, `MC ${n('MC') ? '✓' : '—'}`, `Log ${n('Log') ? '✓' : '—'}`, `WS ${n('WM') || '—'}`].join(' · ');
}
function CoverageLine({ arts }) {
  return <div style={{ fontSize: '0.78rem', color: 'var(--ink-faint)' }}>{coverageText(arts)}</div>;
}

const whiteLink = { background: 'none', border: '1px solid rgba(255,255,255,0.5)', color: '#fff', borderRadius: 'var(--radius-md)', padding: '4px 12px', cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'var(--font-body)' };

// Full-screen viewer. Image → picture; PDF → the browser's PDF view; Word → a readable page
// (converted on the server); video → player; anything else (PowerPoint, ZIP…) → a clean card
// with Download, instead of an empty frame. Click backdrop / Esc / Close to dismiss.
function Lightbox({ artifact, onClose }) {
  const [url, setUrl] = useState(null);
  const [doc, setDoc] = useState(null); // { html } | { error }
  const name = artifact?.fileName;
  const kind = !artifact ? null : isImage(name) ? 'image' : isPdf(name) ? 'pdf' : isDocx(name) ? 'docx' : isVideo(name) ? 'video' : 'other';
  useEffect(() => {
    let alive = true, u = null;
    setUrl(null); setDoc(null);
    if (!artifact?.id) return undefined;
    if (kind === 'docx') {
      api.getArtifactPreview(artifact.id).then(d => alive && setDoc(d)).catch(e => alive && setDoc({ error: e.message }));
    } else if (kind !== 'other') {
      api.getArtifactFileURL(artifact.id).then(x => { if (!alive) { if (x) URL.revokeObjectURL(x); return; } u = x; setUrl(x); });
    }
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [artifact, kind]);
  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  if (!artifact) return null;
  let body;
  if (kind === 'other') {
    body = (
      <div style={{ background: '#fff', borderRadius: 8, padding: '28px 32px', maxWidth: 420, textAlign: 'center' }}>
        <FileBadge name={name} />
        <div style={{ fontSize: '0.9rem', margin: '14px 0 6px', fontWeight: 600, wordBreak: 'break-word' }}>{name}</div>
        <div style={{ fontSize: '0.82rem', color: 'var(--ink-soft)', marginBottom: 14 }}>This file type can’t be shown in the browser — download it and open it in {FILE_LABEL[extOf(name)] ? FILE_LABEL[extOf(name)].split(' ')[0] : 'its app'}.</div>
        <Btn size="sm" onClick={() => api.downloadArtifact(artifact.id, name).catch(e => alert(e.message))}>Download</Btn>
      </div>
    );
  } else if (kind === 'docx') {
    body = !doc ? <Spinner /> : doc.error
      ? <div style={{ background: '#fff', borderRadius: 8, padding: 24, maxWidth: 420 }}><Alert type="error">{doc.error}</Alert></div>
      : <div className="bc-doc-preview" style={{ background: '#fff', borderRadius: 4, width: 'min(860px, 92vw)', maxHeight: '80vh', overflowY: 'auto', padding: '28px 34px', fontSize: '0.9rem', lineHeight: 1.5, color: '#222' }}
          dangerouslySetInnerHTML={{ __html: doc.html || '<p>(empty document)</p>' }} />;
  } else if (!url) {
    body = <Spinner />;
  } else if (kind === 'image') {
    body = <img src={url} alt={name} style={{ maxWidth: '92vw', maxHeight: '80vh', objectFit: 'contain', background: '#fff', borderRadius: 4 }} />;
  } else if (kind === 'video') {
    body = <video src={url} controls autoPlay style={{ maxWidth: '92vw', maxHeight: '80vh', background: '#000', borderRadius: 4 }} />;
  } else {
    body = <iframe title={name} src={url} style={{ width: '86vw', height: '80vh', background: '#fff', border: 'none', borderRadius: 4 }} />;
  }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', zIndex: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <style>{'.bc-doc-preview table{border-collapse:collapse;margin:8px 0;width:100%}.bc-doc-preview td,.bc-doc-preview th{border:1px solid #ddd;padding:6px 8px;vertical-align:top}.bc-doc-preview img{max-width:100%;height:auto}.bc-doc-preview p{margin:0 0 6px}'}</style>
      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{body}</div>
      <div onClick={e => e.stopPropagation()} style={{ marginTop: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
        <span style={{ color: '#fff', fontSize: '0.82rem' }}>{name}</span>
        {kind !== 'other' && <button onClick={() => api.downloadArtifact(artifact.id, name).catch(e => alert(e.message))} style={whiteLink}>Download</button>}
        <button onClick={onClose} style={whiteLink}>Close</button>
      </div>
    </div>
  );
}

// One artefact tile — image thumbnail (click to enlarge) or a PDF/file placeholder; download.
function ArtefactTile({ f, onOpen }) {
  const [thumb, setThumb] = useState(null);
  const img = isImage(f.fileName);
  useEffect(() => {
    let alive = true, u = null;
    if (f.hasFile && f.id && img) api.getArtifactFileURL(f.id).then(x => { if (!alive) { if (x) URL.revokeObjectURL(x); return; } u = x; setThumb(x); });
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [f.id, f.hasFile, img]);
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: '#fff' }}>
      <div onClick={() => f.hasFile && onOpen(f)} title={f.hasFile ? 'Click to open' : ''}
        style={{ cursor: f.hasFile ? 'zoom-in' : 'default', height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--canvas-warm)' }}>
        {!f.hasFile
          ? <span style={{ fontSize: '0.78rem', color: 'var(--ink-faint)' }}>pending sync</span>
          : img
            ? (thumb
              ? <img src={thumb} alt={f.fileName} style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#faf9f7' }} />
              : <span style={{ fontSize: '0.78rem', color: 'var(--ink-faint)' }}>loading…</span>)
            : isPdf(f.fileName)
              ? <PdfThumb id={f.id} name={f.fileName} />
              : <FileBadge name={f.fileName} />}
      </div>
      <div style={{ padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: '0.72rem', color: 'var(--ink-soft)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.fileName}>{f.unitCode ? `(${f.unitCode}) ` : ''}{f.fileName}</span>
        {f.hasFile && <button onClick={() => api.downloadArtifact(f.id, f.fileName).catch(e => alert(e.message))} style={dlLink}>download</button>}
      </div>
    </div>
  );
}

function Gallery({ files, onOpen }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, margin: '2px 0 6px' }}>
      {visibleFiles(files).map((f, i) => <ArtefactTile key={f.id || i} f={f} onOpen={onOpen} />)}
    </div>
  );
}
// Non-flipchart artefacts (cards, logs, working sheets, videos) as a compact list.
const NONFC_TYPES = [['MC', 'Memory cards'], ['Log', 'Expedition logs'], ['WM', 'Working sheets'], ['Video', 'Demo videos (internal)'], ['Info', 'Unit info'], ['About', 'About the unit']];
function ArtefactList({ arts }) {
  return NONFC_TYPES.map(([k, label]) => {
    const files = visibleFiles(arts[k]);
    if (!files.length) return null;
    return (
      <div key={k} style={{ marginTop: 12 }}>
        <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 4 }}>{label} <span style={{ color: 'var(--ink-faint)' }}>({files.length})</span></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>{files.map((f, i) => <ArtifactRow key={f.id || i} f={f} />)}</div>
      </div>
    );
  });
}

const isImage = (name) => /\.(png|jpe?g|gif|webp|svg)$/i.test(name || '');
const extOf = (name) => ((name || '').match(/\.([a-z0-9]+)$/i) || [, ''])[1].toLowerCase();
const isPdf = (name) => extOf(name) === 'pdf';
const isDocx = (name) => extOf(name) === 'docx';
const isVideo = (name) => ['mp4', 'webm', 'mov'].includes(extOf(name));
// Files that live in the OneDrive folders but are not material: notes for whoever syncs
// (_README.txt), Office/OS leftovers. Hidden from view only — they stay in the data.
const isHiddenFile = (name) => /^_|(^|\/)readme(\.|$)|\.(txt|md)$|^~\$|desktop\.ini$|\.ds_store$|thumbs\.db$/i.test(name || '');
const visibleFiles = (files) => (files || []).filter(f => !isHiddenFile(f.fileName));
const FILE_LABEL = { docx: 'Word', doc: 'Word', pptx: 'PowerPoint', potx: 'PowerPoint template', ppt: 'PowerPoint', xlsx: 'Excel', zip: 'ZIP archive', emf: 'Drawing (EMF)', pdf: 'PDF', mp4: 'Video' };

// First page of a PDF as a thumbnail — the browser's own PDF renderer in a non-interactive frame,
// loaded only when the tile scrolls into view (a gallery can hold 70+ cards).
function PdfThumb({ id, name }) {
  const ref = React.useRef(null);
  const [visible, setVisible] = useState(false);
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);
  useEffect(() => {
    if (!visible) return undefined;
    let alive = true, u = null;
    api.getArtifactFileURL(id).then(x => { if (!alive) { if (x) URL.revokeObjectURL(x); return; } u = x; setUrl(x); });
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [visible, id]);
  return (
    <div ref={ref} style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: '#faf9f7' }}>
      {url
        ? <iframe title={name} src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=Fit`} tabIndex={-1}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', pointerEvents: 'none' }} />
        : <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78rem', color: 'var(--ink-faint)' }}>{visible ? 'loading…' : 'PDF'}</span>}
    </div>
  );
}

// A clean placeholder for files the browser can't draw (Word, PowerPoint, ZIP…).
function FileBadge({ name }) {
  const ext = extOf(name);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', letterSpacing: '0.04em', color: 'var(--ink)', border: '1.5px solid var(--ink)', borderRadius: 6, padding: '6px 10px' }}>{(ext || 'file').toUpperCase()}</span>
      <span style={{ fontSize: '0.72rem', color: 'var(--ink-faint)' }}>{FILE_LABEL[ext] || 'File'}</span>
    </div>
  );
}

// One artefact file: pending marker, download link, and a thumbnail for images.
function ArtifactRow({ f }) {
  const [thumb, setThumb] = useState(null);
  useEffect(() => {
    let alive = true, url = null;
    if (f.hasFile && f.id && isImage(f.fileName)) {
      api.getArtifactFileURL(f.id).then(u => { if (!alive) { if (u) URL.revokeObjectURL(u); return; } url = u; setThumb(u); });
    }
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [f.id, f.hasFile, f.fileName]);

  return (
    <div style={{ fontSize: '0.82rem', color: 'var(--ink-soft)', display: 'flex', gap: 8, alignItems: 'center' }}>
      <span style={{ color: f.hasFile ? 'var(--teal, #2a8)' : 'var(--ink-faint)' }}>{f.hasFile ? '✓' : '○'}</span>
      {thumb && <img src={thumb} alt="" style={{ height: 34, width: 'auto', borderRadius: 3, border: '1px solid var(--line)' }} />}
      <span>{f.fileName}{f.language && f.language !== 'EN' ? ` · ${f.language}` : ''}</span>
      {f.hasFile
        ? <button onClick={() => api.downloadArtifact(f.id, f.fileName).catch(e => alert(e.message))}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--ink)', textDecoration: 'underline', fontSize: '0.78rem', fontFamily: 'var(--font-body)' }}>download</button>
        : <span style={{ fontSize: '0.7rem', color: 'var(--ink-faint)' }}>pending sync</span>}
    </div>
  );
}

// ── Development Programmes — the catalogue: Measure · Develop · Customise (W2) ──
const AREAS = [
  { key: 'Measure', sub: 'where the role and the people stand', foot: 'External programmes that measure before and after development' },
  { key: 'Develop', sub: 'Hansen Beck development programmes', foot: 'Standard compositions, tested in many rooms' },
  { key: 'Customise', sub: 'the programme adapted for the client', foot: 'From the standard composition to the client’s own programme' },
];
const pill = (dark) => ({
  display: 'inline-block', padding: '7px 14px', borderRadius: 999, fontSize: '0.82rem', fontWeight: 700, textDecoration: 'none',
  background: dark ? '#fff' : 'transparent', color: dark ? 'var(--ink)' : '#fff', border: '1px solid #fff', margin: 4,
});

function Programmes() {
  const { data, error, loading } = useFetch(() => api.getCatalogue());
  if (loading) return <Loading />;
  if (error) return <Alert type="error">{error}</Alert>;
  const list = data?.programmes || [];
  return (
    <>
      <div style={{ display: 'flex', gap: 14, fontSize: '0.8rem', color: 'var(--muted)', margin: '0 0 10px 4px' }}>
        <span>● Described ({list.filter(p => !p.pending && p.described).length})</span>
        <span>◌ In progress ({list.filter(p => p.pending || !p.described).length})</span>
        <span>{list.length} products</span>
      </div>
      <div style={{ background: 'var(--ink)', color: '#fff', borderRadius: 'var(--radius-panel)', padding: '22px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 0 }}>
        {AREAS.map((a, i) => (
          <div key={a.key} style={{ textAlign: 'center', padding: '6px 14px', borderLeft: i ? '1px solid rgba(255,255,255,.18)' : 'none' }}>
            <div style={{ fontSize: '0.68rem', letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.7 }}>{a.key}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', fontWeight: 700, marginTop: 14 }}>{a.key}</div>
            <div style={{ fontSize: '0.68rem', letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.7, margin: '2px 0 12px' }}>{a.sub}</div>
            <div>
              {list.filter(p => p.area === a.key).map(p => (
                <Link key={p.slug} to={`/deliver/progs/${p.slug}`} style={pill(!p.pending && p.described)} title={p.pending ? 'composition pending' : p.description || ''}>
                  {p.name}
                </Link>
              ))}
            </div>
            <div style={{ fontSize: '0.78rem', opacity: 0.8, marginTop: 12 }}>{a.foot}</div>
          </div>
        ))}
      </div>
    </>
  );
}

// A catalogue programme (by slug): description, format, the standard composition day by day.
function CatalogueProgramme({ slug }) {
  const { data, error, loading } = useFetch(() => api.getCatalogueProgramme(slug), [slug]);
  if (loading) return <Loading />;
  if (error) return <Alert type="error">{error}</Alert>;
  const p = data.programme;
  const days = [...new Set(data.slots.map(s => s.day))];
  return (
    <>
      <Breadcrumb items={[{ label: 'All programmes', to: '/deliver/progs' }, { label: p.name }]} />
      <Card style={{ padding: '22px 28px', marginBottom: 14 }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)' }}>{p.area}</div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.9rem', margin: '6px 0 10px' }}>{p.name}</h1>
        {p.description
          ? <p style={{ fontSize: '0.95rem', color: 'var(--body-ink)', lineHeight: 1.6, margin: 0 }}>{p.description}</p>
          : <p style={{ fontSize: '0.9rem', color: 'var(--muted)', margin: 0 }}>Description in progress.</p>}
        {p.format && <div style={{ fontSize: '0.86rem', marginTop: 12 }}><b>Format</b> {p.format}</div>}
        {p.composition && <div style={{ fontSize: '0.86rem', marginTop: 4 }}><b>In Basecamp</b> Deliver → Development Programmes → {p.composition}</div>}
        {p.area !== 'Measure' && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
            {p.area === 'Develop' && <Link to={`/customers/find?programme=${p.slug}`} style={outlineLink}>Find clients for this product</Link>}
            <Link to={p.area === 'Develop' ? `/customers/find?programme=${p.slug}` : '/customers/journeys'} style={darkLink}>Customise for a client →</Link>
          </div>
        )}
      </Card>
      {p.area === 'Customise' && (
        <SectionCard title="Customisation level">
          <p style={{ fontSize: '0.86rem', color: 'var(--body-ink)', margin: '0 0 10px' }}>
            Every design starts from a standard Hansen Beck composition and belongs to one opportunity. Shape it (days, rhythm, format), adapt it Unit by Unit (keep, modify, drop, add), or fill a gap with a new Unit — then confirm it with the client at Gate 2.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {[['Standard', 'the programme as composed'], ['Shaped', 'days, rhythm and format chosen'], ['Adapted', 'Units kept, modified, dropped or added'], ['New', 'a gap filled with a new Unit']].map(([n, sub], i) => (
              <div key={n} style={{ flex: 1, minWidth: 130, padding: '8px 10px', border: '1px solid var(--line)', background: '#fafafa', fontSize: '0.8rem' }}>
                <b style={{ display: 'block', fontFamily: 'var(--font-display)' }}>{i + 1} · {n}</b>{sub}
              </div>
            ))}
          </div>
        </SectionCard>
      )}
      {p.area === 'Develop' && (p.pending ? (
        <SectionCard title="Standard composition">
          <p style={{ fontSize: '0.86rem', color: 'var(--muted)', margin: 0 }}>The composition of this programme is pending in Basecamp. On an opportunity it can be composed from Units in the Design step.</p>
        </SectionCard>
      ) : (
        <SectionCard title={`Standard composition — ${p.composition}`} hint="As published in Basecamp (Deliver › Development Programmes). Customise it on an opportunity: the project → Design.">
          {days.map(day => (
            <div key={day} style={{ marginBottom: 14 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, margin: '10px 0 4px' }}>Day {day}</div>
              <Table
                headers={['Time', 'Content', 'Units']}
                rows={data.slots.filter(s => s.day === day).map(s => [
                  <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{s.time || '—'}</span>,
                  s.content || '—',
                  s.units.length ? s.units.map(u => (
                    <Link key={u.code} to={`/deliver/units/${u.code}`} style={{ display: 'block', textDecoration: 'underline', fontSize: '0.82rem' }}>({u.code}) {u.name || ''}</Link>
                  )) : '—',
                ])}
              />
            </div>
          ))}
        </SectionCard>
      ))}
    </>
  );
}
const darkLink = { display: 'inline-block', background: 'var(--ink)', color: '#fff', padding: '9px 16px', borderRadius: 8, fontSize: '0.86rem', fontWeight: 700, textDecoration: 'none' };
const outlineLink = { ...darkLink, background: '#fff', color: 'var(--ink)', border: '1px solid var(--line)' };

export function ProgrammeDetail() {
  const { id } = useParams();
  if (!/^\d+$/.test(id)) return <BasecampLayout><CatalogueProgramme slug={id} /></BasecampLayout>;
  return <ProgrammeDays id={id} />;
}

function ProgrammeDays({ id }) {
  const { data, error, loading } = useFetch(() => api.getProgramme(id), [id]);

  return (
    <BasecampLayout>
      {loading ? <Loading /> : error ? <Alert type="error">{error}</Alert> : (
        <>
          <Breadcrumb items={[{ label: 'All programmes', to: '/deliver/progs' }, { label: data.programme.name }]} />
          <PageHeader
            title={data.programme.name}
            subtitle={`${data.programme.kind === 'signature' ? 'signature' : data.programme.kind} programme — day by day`}
          />
          {[...new Set(data.slots.map(s => s.day))].map(day => (
            <Card key={day} style={{ padding: '20px 24px', marginBottom: 18 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', fontWeight: 700, marginBottom: 12 }}>Day {day}</div>
              <Table
                headers={['Time', 'Duration', 'Content', 'Units', 'Memory cards']}
                rows={data.slots.filter(s => s.day === day).map(s => [
                  s.time || '—',
                  s.duration || '—',
                  s.content || '—',
                  s.units.length ? s.units.map(u => (
                    <Link key={u.code} to={`/deliver/units/${u.code}`} style={{ display: 'block', textDecoration: 'underline' }}>
                      ({u.code}) {u.name || ''}
                    </Link>
                  )) : '—',
                  s.memoryCards.length ? s.memoryCards.join(', ') : '—',
                ])}
              />
            </Card>
          ))}
        </>
      )}
    </BasecampLayout>
  );
}

// ── Expedition Logs ────────────────────────────────────────────────────────
function Logs() {
  const { data, error, loading } = useFetch(() => api.getLogs());
  if (loading) return <Loading />;
  if (error) return <Alert type="error">{error}</Alert>;
  return (
    <>
      <p style={{ fontSize: '0.86rem', color: 'var(--ink-soft)', marginBottom: 16 }}>
        One log can cover several Units — participants receive the interval's log after each training day.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
        {(data?.logs || []).map(l => (
          <Card key={l.id} style={{ padding: '16px 20px' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 4 }}>{l.title}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--ink-faint)' }}>
              {l.body ? l.body.slice(0, 120) : 'Log text — content migration in progress.'}
            </div>
            {l.body && <div style={{ marginTop: 10 }}><TranslateButton type="log" entityKey={String(l.id)} /></div>}
          </Card>
        ))}
      </div>
    </>
  );
}

// ── Artefakti: Memory Cards / Flipcharts / Working Sheets ─────────────────
// Type pages (Flipchart Drawings / Memory Cards / Working Sheets): a flat gallery of ALL
// files of that type — each opens in the viewer (click to enlarge) and can be downloaded.
function Artefacts({ kind }) {
  const { data, error, loading } = useFetch(() => api.getArtefacts());
  const [lb, setLb] = useState(null);
  if (loading) return <Loading />;
  if (error) return <Alert type="error">{error}</Alert>;

  const typeKey = kind === 'fcs' ? 'FC' : kind === 'mcs' ? 'MC' : 'WM';
  const label = kind === 'fcs' ? 'flipchart drawing(s)' : kind === 'mcs' ? 'memory card(s)' : 'working sheet(s)';
  const files = visibleFiles((data?.all || {})[typeKey])
    .slice()
    .sort((a, b) => (a.fileName || '').localeCompare(b.fileName || ''));
  return (
    <>
      <p style={{ fontSize: '0.86rem', color: 'var(--ink-soft)', marginBottom: 16 }}>
        {files.length} {label}. Click one to enlarge; each can be downloaded. Files arrive with the OneDrive sync; until then each shows “pending sync”.
      </p>
      {files.length === 0
        ? <EmptyState title="Nothing here yet" message="Run “Sync materials from OneDrive” (on the Units page) to pull the files." />
        : <Gallery files={files} onOpen={setLb} />}
      <Lightbox artifact={lb} onClose={() => setLb(null)} />
    </>
  );
}

// ── My Client Deliveries (Faza 3C — "the spine" se ovde završava) ─────────
// Zatvoreni engagementi iz Master-a; day materials paket (run-sheet logistika,
// FC/sheets/memory cards/log — bez craft-a) i "Close the day".

function DayMaterialsModal({ deliveryId, onClose, onClosedDay }) {
  const { data, error, loading } = useFetch(() => api.deliver.getDayMaterials(deliveryId), [deliveryId]);
  const [report, setReport] = useState('');
  const [busy, setBusy] = useState(false);

  async function closeDay() {
    if (!report.trim()) { alert('Write the daily report first.'); return; }
    setBusy(true);
    try { await api.deliver.closeDay(deliveryId, report); onClosedDay(); onClose(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }

  return (
    <Modal open onClose={onClose} title={loading ? 'Day materials' : `Day ${data.day} — ${data.customer}`}>
      {loading ? <Loading /> : error ? <Alert type="error">{error}</Alert> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Badge status={data.status === 'delivered' ? 'completed' : 'active'}>{data.status}</Badge>
            {data.plannedDate && <Badge status="default">{new Date(data.plannedDate).toLocaleDateString()}</Badge>}
          </div>

          <div>
            <SectionLabel>Run-sheet (logistics)</SectionLabel>
            {data.runSheet.length ? (
              <Table
                headers={['Time', 'Content', 'Units']}
                rows={data.runSheet.map(s => [s.time || '—', s.content || '—', (s.units || []).join(', ') || '—'])}
              />
            ) : <div style={{ fontSize: '0.85rem', color: 'var(--ink-faint)' }}>No slots on this day.</div>}
          </div>

          <div>
            <SectionLabel>To prepare & print</SectionLabel>
            <div style={{ fontSize: '0.85rem', lineHeight: 1.9 }}>
              {data.units.map(u => (
                <div key={u.code}>
                  ▪ <Link to={`/deliver/units/${u.code}`} style={{ textDecoration: 'underline' }}>({u.code}) {u.name}</Link>
                  {u.flipcharts ? ` · ${u.flipcharts} FC to draw` : ''}{u.sheets ? ` · ${u.sheets} sheet(s)` : ''}
                  {u.masters ? ` · masters: ${u.masters}` : ''}
                </div>
              ))}
              {data.memoryCards.length > 0 && <div>▪ Memory cards: {data.memoryCards.join(', ')}</div>}
              {data.logs.length > 0 && <div>▪ Expedition Log(s): {data.logs.join(', ')}</div>}
            </div>
          </div>

          <Alert type="info">{data.craftNote}</Alert>

          {data.status !== 'delivered' && (
            <div>
              <SectionLabel>Close the day</SectionLabel>
              <textarea
                value={report}
                onChange={e => setReport(e.target.value)}
                placeholder="Daily report — what happened, how the room responded, what to carry to the next day…"
                style={{
                  width: '100%', minHeight: 90, padding: '10px 12px', borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--line)', fontSize: '0.9rem', fontFamily: 'var(--font-body)',
                  outline: 'none', resize: 'vertical',
                }}
              />
              <div style={{ marginTop: 10, textAlign: 'right' }}>
                <Btn loading={busy} onClick={closeDay}>Close the day & activate homework</Btn>
              </div>
            </div>
          )}
          {data.status === 'delivered' && <Alert type="success">This day is closed. Homework activation task added to My Tasks.</Alert>}
        </div>
      )}
    </Modal>
  );
}

function ClientDeliveries() {
  const [openDelivery, setOpenDelivery] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const { data, error, loading } = useFetch(() => api.deliver.getMyClients(), [reloadKey]);

  if (loading) return <Loading />;
  if (error) return <Alert type="error">{error}</Alert>;

  const clients = data?.clients || [];
  if (!clients.length) {
    return (
      <EmptyState
        icon="🧗"
        title="No client deliverables yet"
        message="Close an engagement in Master → Proposals to move a client here with their programme and day materials."
      />
    );
  }

  return (
    <>
      <Note>
        The client entered in Explore and translated in Master appears here after closing — with their
        programme, day materials pack and "Close the day". Participants are not Basecamp users in v1; the
        trainer distributes.
      </Note>
      <div style={{ display: 'grid', gap: 16 }}>
        {clients.map(c => (
          <Card key={c.projectId} style={{ padding: '18px 22px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', fontWeight: 700 }}>{c.customer}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--ink-faint)' }}>
                  {c.project} · {c.programme || 'programme'}{c.profile ? ` · ${c.profile}` : ''}
                </div>
              </div>
              {c.nextDay != null && <Badge status="active">Next: Day {c.nextDay}</Badge>}
            </div>
            <div style={{ marginTop: 12 }}>
              <Table
                headers={['Day', 'Date', 'Status', '']}
                rows={c.days.map(d => [
                  `Day ${d.day}`,
                  d.plannedDate ? new Date(d.plannedDate).toLocaleDateString() : '—',
                  <Badge status={d.status === 'delivered' ? 'completed' : 'pending'}>{d.status}</Badge>,
                  <Btn size="sm" variant="outline" onClick={() => setOpenDelivery(d.deliveryId)}>Day materials</Btn>,
                ])}
              />
            </div>
          </Card>
        ))}
      </div>
      {openDelivery != null && (
        <DayMaterialsModal
          deliveryId={openDelivery}
          onClose={() => setOpenDelivery(null)}
          onClosedDay={() => setReloadKey(k => k + 1)}
        />
      )}
    </>
  );
}

// ── Proposals — Notes & tools za Deliver fazu (deljena sekcija) ────────────
function Proposals() {
  return (
    <>
      <Note>
        Delivery standards and tools for this phase — the phase's Hansen Beck Toolbox ingredients.
        Open the Hansen Beck Toolbox to see the full set.
      </Note>
      {/* Prebačena sekcija "Notes & tools" iz PhaseOverview (deljena komponenta) */}
      <PhaseNotes phase="D" />
    </>
  );
}

// ── Deliver area (sub routing) ─────────────────────────────────────────────
const TITLES = {
  overview: 'excellence in the room',
  units: 'Units — the catalogue',
  progs: 'Development Programmes',
  logs: 'Expedition Logs',
  mcs: 'Memory Cards',
  fcs: 'Flipchart Drawings',
  ws: 'Working Sheets',
  client: 'My Client Deliveries',
  proposals: 'Proposals',
};

export default function Deliver() {
  const { sub, code } = useParams();
  // Unit record renders inside the Deliver shell (keeps the Deliver submenu on the left).
  if (code) {
    return (
      <BasecampLayout>
        <AreaLayout items={SUBNAV}><UnitRecord code={code} /></AreaLayout>
      </BasecampLayout>
    );
  }
  if (!sub) return <Navigate to="/deliver/overview" replace />;

  let body;
  if (sub === 'overview') body = <PhaseOverview phase="D" />;
  else if (sub === 'units') body = <UnitsCatalogue />;
  else if (sub === 'progs') body = <Programmes />;
  else if (sub === 'logs') body = <Logs />;
  else if (sub === 'mcs' || sub === 'fcs' || sub === 'ws') body = <Artefacts kind={sub} />;
  else if (sub === 'client') body = <ClientDeliveries />;
  else if (sub === 'proposals') body = <Proposals />;
  else return <Navigate to="/deliver/overview" replace />;

  return (
    <BasecampLayout>
      <PageHeader title="Deliver — the training room" subtitle={TITLES[sub]} />
      <AreaLayout items={SUBNAV}>{body}</AreaLayout>
    </BasecampLayout>
  );
}
